import sjcl from 'sjcl';
import { URLS } from './url';
import {
  backendRequest,
  backendRequestReset,
  backendRequestCompleted,
  editMessage,
  selectFolder,
  selectMessage,
  setError,
  setUserCredentials,
  refreshMessageBackendRequestReset
} from '../actions/application';
import { abortControllerWrappers, abortFetch, toJson } from './fetch';
import { FolderTypes, getFolders, isSentFolder } from './folder';
import i18n from './i18n';
import { recoverState } from './indexed-db';
import { setFolders, setFirstSyncImap } from '../actions/folders';
import { setCache } from '../actions/messages';
import {
  resetFolderMessagesCache,
  addSignAndTemplateToMessage
} from './message';
import sanitize from './sanitize';
import { setFormValues } from '../actions/login';
import { getSyncImap, createSyncImap, resetSyncsImap } from './sync-imap';

export const DEFAULT_IMAP_PORT = 993;
export const DEFAULT_IMAP_SSL = true;
export const DEFAULT_SMTP_PORT = 465;
export const DEFAULT_SMTP_SSL = true;
export const DEFAULT_SMTP_TLS = false;

/**
 * @typedef {Object} Credentials
 * @property {string} serverHost - IMAP host
 * @property {string} serverPort - IMAP port
 * @property {string} user - User
 * @property {string} password - Password
 */
/**
 * Performs a login to the IMAP server using the API.
 *
 * If the server validates the credentials, previous state is reloaded (if exists) from IndexedDB.
 *
 * If previous state exists, message cache, folder list, and selected folder will be pre-populated with existing values.
 * An initial call to the backend will be made to refresh the list of messages of the selected folder.
 *
 * If a previous state doesn't exist, an initial load of folders (including children) will be requested to the server.
 *
 * @param dispatch {Dispatch & function}
 * @param credentials {Credentials}
 * @returns {Promise<void>}
 */
export async function login(dispatch, credentials) {
  try {
    window.indexedDB.deleteDatabase('isotope');
    dispatch(backendRequest());
    dispatch(setFormValues(credentials));
    dispatch(setError('authentication', null));
    const url = URLS.LOGIN;
    // Will be used as the key in the IndexedDB
    const userId = sjcl.codec.hex.fromBits(
      sjcl.hash.sha256.hash(`${credentials.serverHost}|${credentials.user}`)
    );
    // Will be used as the encryption password to store state in the IndexedDB
    const hash = sjcl.codec.hex.fromBits(
      sjcl.hash.sha256.hash(
        `${credentials.serverHost}|${credentials.user}|${credentials.password}`
      )
    );

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(credentials)
    });
    dispatch(backendRequestCompleted());
    if (response.ok) {
      const validatedCredentials = await toJson(response);
      if (credentials.userId) {
        try {
          setTimeout(async () => {
            const getSyncsImap = await getSyncImap({ ...credentials });
            if (!getSyncsImap.data) {
              createSyncImap({ ...credentials });
            } else {
              dispatch(setFirstSyncImap(getSyncsImap.data.active));
              resetSyncsImap({ ...credentials });
            }
          }, 1000);
        } catch (error) {
          console.error('Error while getting syncs IMAP:', error);
        }
      }

      dispatch(setUserCredentials(userId, hash, validatedCredentials));
      // Reload data from indexedDb
      const recoveredState = await recoverState(userId, hash);
      if (recoveredState !== null) {
        await dispatch(setCache(recoveredState.messages.cache));
        await dispatch(setFolders(recoveredState.folders.items));
        dispatch(
          selectFolder({
            folderId: recoveredState.application.selectedFolderId
          })
        );
      } else {
        // Retrieve folders with children so folder tree state is complete from first load
        const setFoldersDispatchAction = await getFolders(
          dispatch,
          validatedCredentials,
          true
        );
        // Retrieve and select INBOX folder so that user has something in the screen
        const inbox = setFoldersDispatchAction.payload.find(
          f => f.type === FolderTypes.INBOX
        );
        if (inbox) {
          dispatch(selectFolder(inbox));
          const user = {
            id: userId,
            hash: hash,
            credentials: validatedCredentials
          };
          resetFolderMessagesCache(dispatch, user, inbox, null);
        }
      }
    } else {
      const error = await response.text();
      dispatch(setError('authentication', error));
    }
  } catch (error) {
    dispatch(backendRequestCompleted());
    dispatch(setError('authentication', error.message));
  } finally {
    dispatch(backendRequestCompleted());
  }
}

export function editNewMessage(dispatch, to = [], sign = '') {
  dispatch(
    editMessage({
      to: to,
      cc: [],
      bcc: [],
      attachments: [],
      subject: '',
      content: `<br/><br/><br/><br/>${sign ? sign : ''}`
    })
  );
}

export function editMessageAsNew(dispatch, message) {
  const recipientMapper = r => r.address;
  const to = message.recipients
    .filter(r => r.type === 'To')
    .map(recipientMapper);
  const cc = message.recipients
    .filter(r => r.type === 'Cc')
    .map(recipientMapper);
  const bcc = message.recipients
    .filter(r => r.type === 'Bcc')
    .map(recipientMapper);
  dispatch(editMessage({ ...message, to, cc, bcc }));
}

function buildReplyHeader(originalMessage) {
  const t = i18n.t.bind(i18n);
  const date = new Date(originalMessage.receivedDate);
  const formattedDate = date.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const formattedTime = date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  const from = originalMessage.from.join(', ');
  return `<p>En ${formattedDate} - ${formattedTime} &lt;${from}&gt; ${t('replyAction.wrote')}:</p>`;
}

export function replyMessage(dispatch, originalMessage, lexon) {
  const recipients = [...originalMessage.recipients];
  const recipientMapper = r => r.address;
  const inReplyTo = [originalMessage.messageId];
  const references = originalMessage.references.concat([
    originalMessage.messageId
  ]);
  const replyTo =
    originalMessage.replyTo && originalMessage.replyTo.length > 0
      ? originalMessage.replyTo
      : originalMessage.from;
  let to;
  if (isSentFolder(originalMessage.folder)) {
    // Replying to a message you sent: reply to the people you sent it to,
    // not to yourself (replyTo/from would be your own address here).
    to = recipients.filter(r => r.type === 'To').map(recipientMapper);
  } else {
    to = replyTo;
  }
  const attachments = [];
  const subject = `${(originalMessage.subject && originalMessage.subject?.toLowerCase().indexOf('re:') === 0) ?
     '' : 'Re: '}${originalMessage.subject ? originalMessage.subject : ''}`;

  const replyHeader = buildReplyHeader(originalMessage);
  const content = addSignAndTemplateToMessage(
    `<br/><br/><br/>${replyHeader}<blockquote>${originalMessage.content}</blockquote>`,
    lexon
);
  const cc = [];
  const bcc = [];

  dispatch(
    editMessage({
      inReplyTo,
      references,
      to,
      cc,
      bcc,
      attachments,
      subject,
      content
    })
  );
}

export function replyMessageAll(dispatch, originalMessage, lexon) {
  const recipients = [...originalMessage.recipients];
  const recipientMapper = r => r.address;
  const inReplyTo = [originalMessage.messageId];
  const references = originalMessage.references.concat([
    originalMessage.messageId
  ]);
  const replyTo =
    originalMessage.replyTo && originalMessage.replyTo.length > 0
      ? originalMessage.replyTo
      : originalMessage.from;
  const ownAddress = (lexon && lexon.account ? lexon.account : '').toLowerCase();
  const excludeOwnAddress = address => !address || address.toLowerCase() !== ownAddress;

  const originalToAddresses = recipients
    .filter(r => r.type === 'To')
    .map(recipientMapper);

  // Replying all to a message you sent must not CC yourself back in: replyTo
  // (== your own address here) only belongs in `to` when replying to someone
  // else's message.
  const to = (isSentFolder(originalMessage.folder)
    ? originalToAddresses
    : originalToAddresses.concat(replyTo)
  ).filter(excludeOwnAddress);
  const cc = recipients
    .filter(r => r.type === 'Cc')
    .map(recipientMapper)
    .filter(excludeOwnAddress);
  const bcc = recipients.filter(r => r.type === 'Bcc').map(recipientMapper);
  const attachments = [];
  const subject = `${(originalMessage.subject && originalMessage.subject?.toLowerCase().indexOf('re:') === 0) ?
     '' : 'Re: '}${originalMessage.subject ? originalMessage.subject : ''}`;

  const replyAllHeader = buildReplyHeader(originalMessage);
  const content = addSignAndTemplateToMessage(
    `<br/><br/><br/>${replyAllHeader}<blockquote>${originalMessage.content}</blockquote>`,
    lexon
  );

  dispatch(
    editMessage({
      inReplyTo,
      references,
      to,
      cc,
      bcc,
      attachments,
      subject,
      content
    })
  );
}

export function forwardMessage(dispatch, originalMessage, lexon) {
  const t = i18n.t.bind(i18n);
  const attachments = originalMessage.attachments
    ? [...originalMessage.attachments]
    : [];
  const subject = `${(originalMessage.subject && originalMessage.subject?.toLowerCase().indexOf('fwd:') === 0) ?
     '' : `${t('messageEditor.fwd')} `}${originalMessage.subject ? originalMessage.subject : ''}`;
  const formattedDate = new Date(originalMessage.receivedDate).toLocaleString(
    navigator.language,
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }
  );
  const recipients = [...originalMessage.recipients];
  const recipientMapper = r => r.address;
  const to = recipients.filter(r => r.type === 'To').map(recipientMapper);
  const cc = recipients.filter(r => r.type === 'Cc').map(recipientMapper);
  const optionalCc =
    cc.length > 0
      ? `<b>${t('forwardAction.Cc')}:</b> ${cc.join(', ')}<br/>`
      : '';

  let content = addSignAndTemplateToMessage(originalMessage.content, lexon);

  const forwardedMessage = `
    <p></p>
    <hr/>
    <p>
      <b>${t('forwardAction.From')}:</b> ${originalMessage.from.join(', ')}<br/>
      <b>${t('forwardAction.To')}:</b> ${to.join(', ')}<br/>
      ${optionalCc}
      <b>${t('forwardAction.Date')}:</b> ${formattedDate}<br/>
      <b>${t('forwardAction.Subject')}:</b> ${originalMessage.subject ? originalMessage.subject : ''}<br/>
    </p>
    <br/>
  `;

  content = `${content}${forwardedMessage}`;

  dispatch(
    editMessage({ to: [], cc: [], bcc: [], attachments, subject, content })
  );
}

export function draftMessage(dispatch, originalMessage) {
  const attachments = originalMessage.attachments
    ? [...originalMessage.attachments]
    : [];
  const subject = originalMessage.subject;
  const recipients = [...originalMessage.recipients];
  const recipientMapper = r => r.address;
  const to = recipients.filter(r => r.type === 'To').map(recipientMapper);
  const cc = recipients.filter(r => r.type === 'Cc').map(recipientMapper);
  const bcc = recipients.filter(r => r.type === 'Bcc').map(recipientMapper);

  const content = sanitize.sanitize(originalMessage.content);
  dispatch(
    editMessage({ to: to, cc: cc, bcc: bcc, attachments, subject, content })
  );
}

/**
 * Aborts any active request to read a message from the BE.
 *
 * Clears the selected message in the application.
 *
 * @param dispatch, store's dispatch function
 */
export function clearSelectedMessage(dispatch) {
  abortFetch(abortControllerWrappers.readMessageAbortController);
  dispatch(selectMessage(null));
}

export function handleRackendRequestReset(dispatch) {
  dispatch(backendRequestReset());
}

export function handleRefreshMessageBackendRequestReset(dispatch) {
  dispatch(refreshMessageBackendRequestReset());
}
